import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { qrAPI } from '../services/api';
import QRCode from 'qrcode';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiLink, FiMail, FiPhone, FiWifi, FiUser, FiDownload,
  FiSave, FiSettings, FiPalette, FiSquare, FiCircle
} = FiIcons;

const QRGenerator = () => {
  const { currentWorkspace } = useWorkspace();
  const [qrType, setQrType] = useState('url');
  const [qrData, setQrData] = useState({
    title: '',
    url: '',
    email: '',
    phone: '',
    sms: '',
    wifi: { ssid: '', password: '', security: 'WPA' },
    vcard: {
      name: '', phone: '', email: '', organization: '', url: ''
    }
  });
  const [design, setDesign] = useState({
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    logo: null,
    cornerRadius: 0,
    dotStyle: 'square',
    size: 1024
  });
  const [qrImage, setQrImage] = useState('');
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef();

  const qrTypes = [
    { id: 'url', name: 'Website URL', icon: FiLink },
    { id: 'email', name: 'Email', icon: FiMail },
    { id: 'phone', name: 'Phone', icon: FiPhone },
    { id: 'sms', name: 'SMS', icon: FiPhone },
    { id: 'wifi', name: 'WiFi', icon: FiWifi },
    { id: 'vcard', name: 'Contact Card', icon: FiUser }
  ];

  const generateQRValue = () => {
    switch (qrType) {
      case 'url':
        return qrData.url;
      case 'email':
        return `mailto:${qrData.email}`;
      case 'phone':
        return `tel:${qrData.phone}`;
      case 'sms':
        return `sms:${qrData.sms}`;
      case 'wifi':
        return `WIFI:T:${qrData.wifi.security};S:${qrData.wifi.ssid};P:${qrData.wifi.password};;`;
      case 'vcard':
        return `BEGIN:VCARD
VERSION:3.0
FN:${qrData.vcard.name}
TEL:${qrData.vcard.phone}
EMAIL:${qrData.vcard.email}
ORG:${qrData.vcard.organization}
URL:${qrData.vcard.url}
END:VCARD`;
      default:
        return '';
    }
  };

  const generateQR = async () => {
    const value = generateQRValue();
    if (!value) return;

    try {
      const qrImageUrl = await QRCode.toDataURL(value, {
        width: design.size,
        color: {
          dark: design.foregroundColor,
          light: design.backgroundColor
        },
        margin: 1
      });
      setQrImage(qrImageUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const handleSave = async () => {
    if (!qrImage || !qrData.title) return;

    setLoading(true);
    try {
      const qrPayload = {
        workspaceId: currentWorkspace.id,
        title: qrData.title,
        type: qrType,
        data: qrData,
        design: design,
        image: qrImage
      };

      await qrAPI.create(qrPayload);
      // Reset form or show success message
      setQrData({ title: '', url: '', email: '', phone: '', sms: '', wifi: { ssid: '', password: '', security: 'WPA' }, vcard: { name: '', phone: '', email: '', organization: '', url: '' } });
      setQrImage('');
    } catch (error) {
      console.error('Failed to save QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrImage) return;

    const link = document.createElement('a');
    link.download = `${qrData.title || 'qr-code'}.png`;
    link.href = qrImage;
    link.click();
  };

  const renderQRForm = () => {
    switch (qrType) {
      case 'url':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL
            </label>
            <input
              type="url"
              value={qrData.url}
              onChange={(e) => setQrData(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );
      
      case 'email':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={qrData.email}
              onChange={(e) => setQrData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="contact@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );
      
      case 'phone':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={qrData.phone}
              onChange={(e) => setQrData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+1234567890"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );
      
      case 'wifi':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Network Name (SSID)
              </label>
              <input
                type="text"
                value={qrData.wifi.ssid}
                onChange={(e) => setQrData(prev => ({ 
                  ...prev, 
                  wifi: { ...prev.wifi, ssid: e.target.value }
                }))}
                placeholder="MyWiFiNetwork"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={qrData.wifi.password}
                onChange={(e) => setQrData(prev => ({ 
                  ...prev, 
                  wifi: { ...prev.wifi, password: e.target.value }
                }))}
                placeholder="WiFi password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Security Type
              </label>
              <select
                value={qrData.wifi.security}
                onChange={(e) => setQrData(prev => ({ 
                  ...prev, 
                  wifi: { ...prev.wifi, security: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">No Password</option>
              </select>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">QR Code Generator</h1>
        <p className="mt-1 text-sm text-gray-500">
          Create custom QR codes for your business needs
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* QR Type Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {qrTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setQrType(type.id)}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    qrType === type.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={type.icon} className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm font-medium">{type.name}</p>
                </button>
              ))}
            </div>
          </motion.div>

          {/* QR Data Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Content</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR Code Title
                </label>
                <input
                  type="text"
                  value={qrData.title}
                  onChange={(e) => setQrData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="My QR Code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {renderQRForm()}
            </div>
          </motion.div>

          {/* Design Customization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Design</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foreground Color
                </label>
                <input
                  type="color"
                  value={design.foregroundColor}
                  onChange={(e) => setDesign(prev => ({ ...prev, foregroundColor: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <input
                  type="color"
                  value={design.backgroundColor}
                  onChange={(e) => setDesign(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size (px)
                </label>
                <select
                  value={design.size}
                  onChange={(e) => setDesign(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={512}>512x512</option>
                  <option value={1024}>1024x1024</option>
                  <option value={2048}>2048x2048</option>
                  <option value={4096}>4096x4096</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex space-x-4">
              <button
                onClick={generateQR}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Generate QR Code
              </button>
            </div>
          </motion.div>
        </div>

        {/* Preview Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          
          <div className="flex flex-col items-center">
            {qrImage ? (
              <div className="mb-4">
                <img
                  src={qrImage}
                  alt="Generated QR Code"
                  className="w-64 h-64 border border-gray-200 rounded-lg"
                />
              </div>
            ) : (
              <div className="w-64 h-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4">
                <div className="text-center">
                  <SafeIcon icon={FiSettings} className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Configure and generate</p>
                </div>
              </div>
            )}

            {qrImage && (
              <div className="w-full space-y-3">
                <button
                  onClick={handleDownload}
                  className="w-full flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                  Download
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={loading || !qrData.title}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  )}
                  Save to Workspace
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QRGenerator;